import { addDays, addWeeks, format, startOfWeek, subWeeks } from "date-fns";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Scissors,
  User,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

interface Appointment {
  id: string;
  clientName: string;
  service: string;
  date: Date;
  time: string;
  duration: number;
  status: "scheduled" | "in-progress" | "completed" | "cancelled";
  notes?: string;
}

const Appointments = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      id: "1",
      clientName: "Sarah Johnson",
      service: "Haircut & Style",
      date: new Date(),
      time: "10:00",
      duration: 60,
      status: "scheduled",
      notes: "Regular client, prefers shorter styles",
    },
    {
      id: "2",
      clientName: "Mike Smith",
      service: "Beard Trim",
      date: new Date(),
      time: "11:30",
      duration: 30,
      status: "in-progress",
    },
    {
      id: "3",
      clientName: "Emma Davis",
      service: "Color Treatment",
      date: new Date(),
      time: "14:00",
      duration: 120,
      status: "completed",
      notes: "Allergic to certain dyes",
    },
  ]);

  const weekDays = Array.from({ length: 7 }, (_, i) =>
    addDays(startOfWeek(selectedDate), i)
  );

  const timeSlots = Array.from({ length: 12 }, (_, i) =>
    format(new Date().setHours(9 + Math.floor(i), (i % 1) * 60), "HH:mm")
  );

  const handlePreviousWeek = () => {
    setSelectedDate(subWeeks(selectedDate, 1));
  };

  const handleNextWeek = () => {
    setSelectedDate(addWeeks(selectedDate, 1));
  };

  const handleStatusChange = (
    appointmentId: string,
    newStatus: Appointment["status"]
  ) => {
    setAppointments(
      appointments.map((apt) =>
        apt.id === appointmentId ? { ...apt, status: newStatus } : apt
      )
    );
    toast.success(`Appointment status updated to ${newStatus}`);
  };

  const handleAddAppointment = (formData: FormData) => {
    const newAppointment: Appointment = {
      id: Math.random().toString(),
      clientName: formData.get("clientName") as string,
      service: formData.get("service") as string,
      date: new Date(formData.get("date") as string),
      time: formData.get("time") as string,
      duration: Number(formData.get("duration")),
      status: "scheduled",
      notes: formData.get("notes") as string,
    };

    setAppointments([...appointments, newAppointment]);
    setIsAddModalOpen(false);
    toast.success("New appointment scheduled successfully");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold text-gray-900">Appointments</h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePreviousWeek}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5 text-gray-500" />
            </button>
            <span className="text-sm font-medium text-gray-900">
              {format(weekDays[0], "MMM d")} -{" "}
              {format(weekDays[6], "MMM d, yyyy")}
            </span>
            <button
              onClick={handleNextWeek}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronRight className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Appointment
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg shadow">
        <div className="grid grid-cols-8 gap-px bg-gray-200 border-b">
          <div className="bg-white p-4">
            <span className="text-sm font-medium text-gray-500">Time</span>
          </div>
          {weekDays.map((day, i) => (
            <div
              key={i}
              className={`bg-white p-4 text-center ${
                format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")
                  ? "bg-primary-50"
                  : ""
              }`}
            >
              <span className="text-sm font-medium text-gray-900">
                {format(day, "EEE")}
              </span>
              <br />
              <span className="text-sm text-gray-500">
                {format(day, "MMM d")}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-8 gap-px bg-gray-200">
          {timeSlots.map((time, i) => (
            <React.Fragment key={time}>
              <div className="bg-white p-4">
                <span className="text-sm text-gray-500">{time}</span>
              </div>
              {weekDays.map((day, j) => (
                <div
                  key={`${i}-${j}`}
                  className="bg-white p-2 min-h-[100px] relative"
                >
                  {appointments
                    .filter(
                      (apt) =>
                        format(apt.date, "yyyy-MM-dd") ===
                          format(day, "yyyy-MM-dd") && apt.time === time
                    )
                    .map((appointment) => (
                      <motion.div
                        key={appointment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`absolute inset-x-2 p-2 rounded-lg ${
                          appointment.status === "completed"
                            ? "bg-green-100"
                            : appointment.status === "in-progress"
                            ? "bg-blue-100"
                            : appointment.status === "cancelled"
                            ? "bg-red-100"
                            : "bg-primary-100"
                        }`}
                        style={{
                          height: `${(appointment.duration / 30) * 50}px`,
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {appointment.clientName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {appointment.service}
                            </p>
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={() =>
                                handleStatusChange(appointment.id, "completed")
                              }
                              className="p-1 rounded-full hover:bg-white"
                              title="Mark as completed"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusChange(appointment.id, "cancelled")
                              }
                              className="p-1 rounded-full hover:bg-white"
                              title="Cancel appointment"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        {appointment.notes && (
                          <div className="mt-1 flex items-center">
                            <AlertCircle className="h-3 w-3 text-gray-400 mr-1" />
                            <p className="text-xs text-gray-500 truncate">
                              {appointment.notes}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Add Appointment Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Schedule New Appointment
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddAppointment(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Client Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="clientName"
                    required
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Scissors className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="service"
                    required
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="Haircut & Style">Haircut & Style</option>
                    <option value="Color Treatment">Color Treatment</option>
                    <option value="Beard Trim">Beard Trim</option>
                    <option value="Manicure">Manicure</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      name="date"
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Time
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      name="time"
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  name="duration"
                  defaultValue="60"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md"
                >
                  Schedule Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
